import { blogSource } from "./content-sources";
import { articleCards } from "./marketing-catalog";
import redirects from "./blog-redirects.json";
import {
  absoluteUrl,
  canonicalUrl,
  SITE_NAME,
  SITE_URL,
  organizationSchema,
  websiteSchema,
  breadcrumbSchema,
  faqSchema,
} from "@/lib/seo";
export type BlogMetadata = {
  slug: string;
  title: string;
  heading?: string;
  description: string;
  datePublished?: string;
  dateModified?: string;
  status: string;
  keywords?: string[];
  image?: string;
  faqs?: { question: string; answer: string }[];
};
export const blogPosts: BlogMetadata[] = articleCards("blog").map(({ url }) => {
  const data = blogSource.getPage([url.slice("/blog/".length)])!.data;
  return {
    slug: url.slice("/blog/".length),
    title: data.title,
    heading: data.heading,
    description: data.description ?? "",
    datePublished: data.datePublished,
    dateModified: data.dateModified ?? data.datePublished,
    status: "published",
    keywords: data.keywords,
    image: data.image,
    faqs: data.faqs,
  };
});
export const blogHub = blogSource.getPage([])!.data;
export function findBlogRedirect(slug: string): string | undefined {
  return Object.hasOwn(redirects, slug) ? redirects[slug as keyof typeof redirects] : undefined;
}
export function blogSchemas(post?: BlogMetadata) {
  const path = post ? "/blog/" + post.slug : "/blog";
  const schemas: unknown[] = [
    organizationSchema(),
    websiteSchema(),
    ...(post ? [blogPostingSchema({ ...post, path })] : []),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blog" },
      ...(post ? [{ name: post.title, path }] : []),
    ]),
  ];
  if (post?.faqs?.length) schemas.push(faqSchema(post.faqs));
  return schemas;
}
export const DEFAULT_BLOG_OG_IMAGE = "/og/blog-default.png";

type BlogPostingInput = {
  title: string;
  description: string;
  path: string;
  datePublished?: string;
  dateModified?: string;
  image?: string;
  keywords?: string[];
};

/**
 * Schema.org BlogPosting — used for individual blog post pages.
 * Enforces `dateModified >= datePublished` at runtime to prevent nonsensical
 * freshness signals from reaching crawlers.
 */
export function blogPostingSchema({
  title,
  description,
  path,
  datePublished,
  dateModified,
  image = DEFAULT_BLOG_OG_IMAGE,
  keywords,
}: BlogPostingInput) {
  if (dateModified && datePublished && dateModified < datePublished) {
    throw new Error(
      `blogPostingSchema: dateModified (${dateModified}) must be >= datePublished (${datePublished}) for ${path}`,
    );
  }
  const url = canonicalUrl(path);
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description,
    url,
    image: absoluteUrl(image),
    inLanguage: "en",
    datePublished,
    dateModified,
    author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: absoluteUrl("/logo-light.svg") },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    ...(keywords?.length ? { keywords: keywords.join(", ") } : {}),
  };
}
